import { App } from '@capacitor/app'
import { Network } from '@capacitor/network'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import {
  enqueueDelete as libEnqueue,
  flushQueue as libFlush,
  listQueued as libList,
  type QueuedDelete,
} from '@/lib/delete-queue'

/**
 * Pinia wrapper around the offline delete-queue lib. Same lifecycle
 * shape as `useRunQueueStore`: a thin reactive view over the on-disk
 * `@capacitor/preferences` queue, plus the wiring that decides when
 * to flush.
 *
 * Exposes a reactive `pendingIds` Set so the exercises store can
 * filter a server-refresh result without having to re-read Preferences
 * every render. The Set stays in sync with `pending` through
 * `refreshPending()`, which every mutating action calls.
 */
export const useDeleteQueueStore = defineStore('deleteQueue', () => {
  const pending = ref<number>(0)
  const pendingIds = ref<Set<number>>(new Set())
  let installed = false

  /** Convenience predicate for `useExercisesStore.refresh()`. */
  const isPending = computed(() => (id: number) => pendingIds.value.has(id))

  async function refreshPending(): Promise<void> {
    const list: QueuedDelete[] = await libList()
    pending.value = list.length
    pendingIds.value = new Set(list.map((q) => q.exercise_id))
  }

  /**
   * Enqueue a deletion, attempt a flush, then refresh the reactive view.
   *
   * `libEnqueue` already fires an opportunistic `void flushQueue()`, but
   * we also explicitly await `libFlush()` here so the caller's pending
   * count reflects the post-flush state instead of the briefly-stale
   * "queued but mid-flight" state. The double-flush is harmless — the
   * second pass reads an empty queue when the first one succeeded
   * online, and idempotently retries when the first one is still in
   * flight or failed. Offline both flushes fail fast (axios rejects
   * without a round-trip) and the entry stays put, so the picker's
   * pending pill renders correctly on the first paint.
   */
  async function enqueue(exerciseId: number): Promise<void> {
    await libEnqueue(exerciseId)
    await libFlush()
    await refreshPending()
  }

  async function flush(): Promise<void> {
    await libFlush()
    await refreshPending()
  }

  /**
   * Wire up the lifecycle subscribers. Idempotent — calling install
   * twice attaches one set of listeners. Same triggers as the run
   * queue: app boot (the install call itself), network reconnect,
   * and foreground-resume (iOS sometimes misses networkStatusChange
   * across a background trip).
   */
  async function install(): Promise<void> {
    if (installed) return
    installed = true
    await refreshPending()

    // Kick at install time so leftovers from a previous offline session
    // go out as soon as the patient opens the app online.
    void flush()

    void Network.addListener('networkStatusChange', (status) => {
      if (status.connected) {
        void flush()
      }
    })

    void App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        void flush()
      }
    })
  }

  return {
    // state
    pending,
    pendingIds,
    // getters
    isPending,
    // actions
    install,
    enqueue,
    flush,
    refreshPending,
  }
})
